/**
 * CustomerGroupSelectionModal - Auto-triggered modal for department selection
 * Mirrors the mobile app's CustomerGroupSelectionModal behavior:
 * - Non-closeable when triggered automatically (user must select)
 * - Closeable when opened from settings
 * Created by claude on 09.03.26
 */
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { useTrans } from '../Store/System/LocalizationStore';
import { Theme } from '../Theme/Theme';
import { CustomerGroup, CustomerGroupApi } from '../Model/CustomerGroup/CustomerGroup';
import { Button } from './Button';

const ModalContainer = styled.div`
  padding: 8px 0;
`;

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  text-align: center;
  margin: 0 0 8px 0;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  text-align: center;
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const DepartmentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const DepartmentOption = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 12px;
  background: ${(props) => (props.$selected ? Theme.Colors.successOpacity : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? Theme.Colors.successOpacity : Theme.Colors.lightestGrey)};
  }
`;

const DepartmentIcon = styled.div<{ $selected?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 22px;
    height: 22px;
    color: ${(props) => (props.$selected ? 'white' : Theme.Colors.warmGrey)};
  }
`;

const DepartmentContent = styled.div`
  flex: 1;
`;

const DepartmentName = styled.div<{ $selected?: boolean }>`
  font-size: 15px;
  font-weight: ${(props) => (props.$selected ? '600' : '400')};
  color: ${(props) => (props.$selected ? Theme.Colors.primaryDarkest : Theme.Colors.textColor)};
`;

const DepartmentDescription = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  margin-top: 2px;
`;

const RadioIndicator = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
    transition: all 0.2s ease;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const ConfirmButton = styled(Button)`
  width: 100%;
`;

const GroupIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export type CustomerGroupSelectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  closeable?: boolean;
};

export const CustomerGroupSelectionModal: React.FC<CustomerGroupSelectionModalProps> = observer(
  ({ isOpen, onClose, closeable = false }) => {
    const { t } = useTrans();

    const [groups, setGroups] = useState<CustomerGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);

    useEffect(() => {
      if (isOpen) {
        setLoading(true);
        Promise.all([CustomerGroupApi.list(), CustomerGroupApi.current()])
          .then(([groupList, currentGroup]) => {
            setGroups(groupList);
            if (currentGroup) {
              setSelectedGroupId(currentGroup.id);
            }
          })
          .finally(() => setLoading(false));
      }
    }, [isOpen]);

    const handleSelect = useCallback((groupId: string) => {
      setSelectedGroupId(groupId);
    }, []);

    const handleConfirm = useCallback(async () => {
      if (!selectedGroupId) return;
      setSaving(true);
      try {
        await CustomerGroupApi.setCurrent(selectedGroupId);
        onClose();
      } catch (error) {
        console.error('Error saving department:', error);
      } finally {
        setSaving(false);
      }
    }, [selectedGroupId, onClose]);

    return (
      <Modal
        isOpen={isOpen}
        toggle={closeable ? onClose : undefined}
        centered
        backdrop={closeable ? true : 'static'}
        keyboard={closeable}
      >
        <ModalBody>
          <ModalContainer>
            <ModalTitle>
              {t('customerGroupSelectionModal.title', { defaultValue: 'Department' })}
            </ModalTitle>
            <ModalDescription>
              {t('customerGroupSelectionModal.hint', {
                defaultValue:
                  "Don't worry \u2013 we never share personal data. Your employer only sees that someone is assigned to a department. Please select the department you belong to.",
              })}
            </ModalDescription>

            {loading ? (
              <LoadingContainer>
                <Spinner color="primary" />
              </LoadingContainer>
            ) : (
              <DepartmentGrid>
                {groups.map((group) => {
                  const selected = selectedGroupId === group.id;
                  return (
                    <DepartmentOption key={group.id} $selected={selected} onClick={() => handleSelect(group.id)}>
                      <DepartmentIcon $selected={selected}>
                        <GroupIcon />
                      </DepartmentIcon>
                      <DepartmentContent>
                        <DepartmentName $selected={selected}>{group.name}</DepartmentName>
                        {group.description && <DepartmentDescription>{group.description}</DepartmentDescription>}
                      </DepartmentContent>
                      <RadioIndicator $selected={selected} />
                    </DepartmentOption>
                  );
                })}
              </DepartmentGrid>
            )}

            <ConfirmButton
              color="primary"
              onClick={handleConfirm}
              disabled={saving || !selectedGroupId}
            >
              {saving
                ? t('common.saving', { defaultValue: 'Saving...' })
                : t('globals.continue', { defaultValue: 'Continue' })}
            </ConfirmButton>
          </ModalContainer>
        </ModalBody>
      </Modal>
    );
  },
);
